import React, { useMemo, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { useSelector } from "react-redux"
import ListingTabs from "../../components/Listing/ListingTabs"
import CustomButton from "../../components/OnboardingComponents/CustomButton"
import { Avatar } from "../../components/DashboardComponents/AvatarGroup"
import MediaFallbackImage from "../../components/Common/MediaFallbackImage"
import { useCreateFlagModal } from "../../components/Modals/CreateFlagModal"
import { ICONS } from "../../constants/icons"
import { getUserAvatarUrl } from "../../helpers/userAvatar"
import { getUserFullName } from "../../helpers/userDisplay"
import {
  INBOX,
  INBOX_COMMENTS,
  INBOX_RESPONSES,
} from "./cityHubData"
import {
  VISIBLE_CITY_HUB_TABS,
  cityHubTabPath,
  getCityHubTabByPath,
} from "./cityHubTabs"
import { CityHubAside, CityHubBanner, CityHubGenome } from "./CityHubPanels"
import {
  getCityHubEngageRestriction,
  getUserCityHubLevel,
  mapUserAddressesToExploreCities,
  pickDefaultCityHubCity,
} from "./cityHubCity"

const SORT_OPTIONS = [
  { value: "recent", label: "Most Recent" },
  { value: "liked", label: "Most Liked" },
]

const roleToneClass = (badge = "") => {
  const key = badge.toLowerCase()
  if (key.includes("freelance")) return "city-hub-call__role--muted"
  if (key.includes("author")) return "city-hub-call__role--author"
  if (key.includes("photo")) return "city-hub-call__role--purple"
  return "city-hub-call__role--warm"
}

/**
 * City Inbox detail — Figma: tabs only, hero card, threaded responses + Flag/Report.
 */
const CityHubInboxDetail = () => {
  const { inboxId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const currentUser = useSelector(state => state.auth.currentUser)
  const { openCreateFlag } = useCreateFlagModal()
  const [sortBy, setSortBy] = useState("recent")
  const [replyText, setReplyText] = useState("")
  const [openReplyId, setOpenReplyId] = useState(null)

  const yourCities = useMemo(
    () => mapUserAddressesToExploreCities(currentUser),
    [currentUser],
  )

  const userLevel = useMemo(
    () => getUserCityHubLevel(currentUser),
    [currentUser],
  )

  const city = useMemo(() => {
    const fromState = location.state?.city
    if (fromState?.id) return fromState
    const stateCityId = location.state?.cityId
    if (stateCityId) {
      return (
        yourCities.find(item => String(item.id) === String(stateCityId)) ||
        null
      )
    }
    return pickDefaultCityHubCity(currentUser)
  }, [currentUser, location.state, yourCities])

  const cityId = location.state?.cityId || city?.id || null
  const engageRestriction = useMemo(
    () => getCityHubEngageRestriction(cityId, yourCities, userLevel),
    [cityId, yourCities, userLevel],
  )
  const canEngage = Boolean(engageRestriction.canEngage)

  const composerUser = useMemo(
    () => ({
      name: getUserFullName(currentUser, { fallback: "You" }),
      avatar: getUserAvatarUrl(currentUser),
    }),
    [currentUser],
  )

  const item = useMemo(
    () => INBOX.find(row => row.id === Number(inboxId)) || INBOX[0],
    [inboxId],
  )

  const responses = useMemo(() => {
    const list = [...(INBOX_RESPONSES[item.id] || [])]
    if (sortBy === "liked") {
      list.sort((a, b) => (b.likes || 0) - (a.likes || 0))
    }
    return list
  }, [item.id, sortBy])

  const tabItems = useMemo(
    () =>
      VISIBLE_CITY_HUB_TABS.map(tab => ({
        key: tab.path,
        label: tab.label,
        icon: tab.icon,
      })),
    [],
  )

  const handleTabChange = key => {
    const nextTab = getCityHubTabByPath(key)
    if (nextTab) navigate(cityHubTabPath(nextTab, cityId))
  }

  const description = item.detailDescription || item.description
  const postedLabel = item.postedLong || item.posted

  return (
    <div className="page-content">
      <div className="city-hub city-hub--detail">
        <CityHubBanner
          city={city}
          yourCities={yourCities}
          canExplore={false}
        />
        <CityHubGenome city={city} />

        <div className="city-hub__main">
          <div className="city-hub__tabs-row">
            <ListingTabs
              items={tabItems}
              activeKey="city-inbox"
              onChange={handleTabChange}
            />
          </div>

          <div className="city-hub-call">
            <article className="city-hub-call__hero">
              <MediaFallbackImage
                src={item.image}
                fallback={ICONS.thumbnailPlaceholder}
                alt=""
                className="city-hub-call__poster"
              />

              <div className="city-hub-call__body">
                <div className="city-hub-call__title-row">
                  <h1 className="city-hub-call__title">{item.title}</h1>
                  <div className="city-hub-call__status-wrap">
                    <span className="city-hub-call__badge">
                      {item.active ? "Active" : item.status || "Active"}
                    </span>
                    {item.deadline ? (
                      <span className="city-hub-call__deadline">
                        {item.deadline}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="city-hub-call__lower">
                  <div className="city-hub-call__copy">
                    <p className="city-hub-call__desc">{description}</p>
                    <div className="city-hub-call__meta">
                      <span className="city-hub-call__location">
                        <img
                          src={ICONS.callLocationGreen}
                          alt=""
                          width={15}
                          height={15}
                        />
                        {item.city || "Global"}
                      </span>
                      {item.category ? (
                        <span className="city-hub-call__category">
                          {item.category}
                        </span>
                      ) : null}
                      <span className="city-hub-call__posted">
                        Posted by: {item.poster?.name}
                        {postedLabel ? `, ${postedLabel}` : ""}
                      </span>
                      <span className="city-hub-call__dot" aria-hidden="true">
                        •
                      </span>
                      <span className="city-hub-call__count">
                        {item.responses} responses
                      </span>
                    </div>
                  </div>

                  <div className="city-hub-call__entities">
                    <div className="city-hub-call__entity city-hub-call__entity--inbox">
                      <Avatar
                        src={item.poster?.avatar}
                        name={item.poster?.name}
                        size="sm"
                        className="city-hub-call__entity-avatar"
                      />
                      <div className="city-hub-call__entity-text">
                        <strong>
                          {item.project?.name || item.poster?.name}
                        </strong>
                        <span>Posted by: {item.poster?.name}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </article>

            <div className="city-hub-call__responses">
              <div className="city-hub-call__responses-head">
                <h2 className="city-hub-call__responses-title">
                  {item.responses} Responses
                </h2>
                <label className="city-hub-call__sort">
                  <span>Sort by:</span>
                  <select
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value)}
                    aria-label="Sort responses"
                  >
                    {SORT_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <img src={ICONS.chevronDown} alt="" width={14} height={14} />
                </label>
              </div>

              <div className="city-hub-call__threads">
                {responses.map(response => {
                  const comments = INBOX_COMMENTS[response.id] || []
                  return (
                    <article key={response.id} className="city-hub-call__thread">
                      <Avatar
                        src={response.avatar}
                        name={response.name}
                        size="md"
                        className="city-hub-call__thread-avatar"
                      />
                      <div className="city-hub-call__thread-body">
                        <div className="city-hub-call__thread-head">
                          <div className="city-hub-call__thread-meta">
                            <span className="city-hub-call__thread-name">
                              {response.name}
                            </span>
                            {response.badge ? (
                              <span
                                className={`city-hub-call__role ${roleToneClass(
                                  response.badge,
                                )}`}
                              >
                                {response.badge}
                              </span>
                            ) : null}
                            <span className="city-hub-call__thread-time">
                              {response.time}
                            </span>
                          </div>
                          <CustomButton
                            variant="secondary"
                            size="sm"
                            type="button"
                            className="city-hub-call__flag-btn"
                            trailingIconSrc={ICONS.activityFlag}
                            trailingIconAlt=""
                            onClick={() =>
                              openCreateFlag({
                                name: response.name,
                                avatar: response.avatar,
                                moduleKey: "member_profile",
                                moduleLabel: "Member Profile",
                              })
                            }
                          >
                            Flag/Report
                          </CustomButton>
                        </div>

                        <p className="city-hub-call__thread-text">
                          {response.text}
                        </p>

                        <div className="city-hub-call__actions-row">
                          <button
                            type="button"
                            className="city-hub-call__action"
                            aria-label="Like response"
                            title={
                              canEngage ? undefined : engageRestriction.hint
                            }
                            disabled={!canEngage}
                          >
                            <img
                              src={ICONS.heart}
                              alt=""
                              width={14}
                              height={14}
                            />
                            <span>{response.likes || 0}</span>
                          </button>
                          <button
                            type="button"
                            className="city-hub-call__action city-hub-call__action--comments"
                            disabled
                          >
                            <img
                              src={ICONS.messageText}
                              alt=""
                              width={14}
                              height={14}
                            />
                            <span>{response.replies || 0}</span>
                          </button>
                          <button
                            type="button"
                            className="city-hub-call__reply-link"
                            title={
                              canEngage ? undefined : engageRestriction.hint
                            }
                            disabled={!canEngage}
                            onClick={() => {
                              if (!canEngage) return
                              setOpenReplyId(prev =>
                                prev === response.id ? null : response.id,
                              )
                            }}
                          >
                            Reply
                          </button>
                        </div>

                        {comments.length > 0 ? (
                          <div className="city-hub-call__replies">
                            {comments.map(comment => (
                              <div
                                key={comment.id}
                                className="city-hub-call__reply-card"
                              >
                                <div className="city-hub-call__reply-head">
                                  <Avatar
                                    src={comment.avatar}
                                    name={comment.name}
                                    size="xs"
                                    className="city-hub-call__reply-avatar"
                                  />
                                  <div className="city-hub-call__reply-meta">
                                    <div className="city-hub-call__reply-names">
                                      <span>{comment.name}</span>
                                      {comment.author ? (
                                        <span className="city-hub-call__author">
                                          Author
                                        </span>
                                      ) : null}
                                    </div>
                                    <span className="city-hub-call__thread-time">
                                      {comment.time}
                                    </span>
                                  </div>
                                </div>
                                <p className="city-hub-call__reply-text">
                                  {comment.text}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : null}

                        {canEngage && openReplyId === response.id ? (
                          <div className="city-hub-call__inline-reply">
                            <input
                              type="text"
                              placeholder="Write a reply..."
                              aria-label={`Reply to ${response.name}`}
                            />
                            <CustomButton
                              variant="primary"
                              size="sm"
                              type="button"
                            >
                              Reply
                            </CustomButton>
                          </div>
                        ) : null}
                      </div>
                    </article>
                  )
                })}

                {responses.length === 0 ? (
                  <div className="city-hub-call__empty">No responses yet.</div>
                ) : null}
              </div>

              <div className="city-hub-call__composer-dock">
                {!canEngage && engageRestriction.context ? (
                  <p className="city-hub-call__composer-context">
                    {engageRestriction.context}
                  </p>
                ) : null}
                <div
                  className={`city-hub-call__composer${
                    !canEngage ? " city-hub-call__composer--disabled" : ""
                  }`}
                >
                  <Avatar
                    src={composerUser.avatar}
                    name={composerUser.name}
                    size="sm"
                    className="city-hub-call__composer-avatar"
                  />
                  <div className="city-hub-call__composer-field">
                    <input
                      type="text"
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      placeholder={engageRestriction.placeholder}
                      aria-label="Write a response"
                      title={canEngage ? undefined : engageRestriction.hint}
                      disabled={!canEngage}
                    />
                    <button
                      type="button"
                      className="city-hub-call__composer-send"
                      aria-label="Send response"
                      title={canEngage ? undefined : engageRestriction.hint}
                      disabled={!canEngage || !replyText.trim()}
                      onClick={() => {
                        if (!canEngage) return
                        setReplyText("")
                      }}
                    >
                      <img src={ICONS.send} alt="" width={16} height={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <CityHubAside city={city} />
      </div>
    </div>
  )
}

export default CityHubInboxDetail
